import { IconButton, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigationStore } from '../../../../../../store';
import {
  Divider,
  StyledPhoneInput,
} from '@/components/navigation/NavigationIntroScreen/components/Popup/styles';
import { SendIcon } from '@/components/navigation/NavigationIntroScreen/components/Popup/Icons';
import { navigationTranslationState } from '../../../../../../store';
import AdminAPI from '@/api/TreedisAdminDAO';
import { RatingTooltip } from '@/components/navigation/NavigationIntroScreen/components/Popup/RatingTooltip';

type Props = {
  popupTitle?: string;
  qrEnabled: boolean;
  qrTitle?: string;
  smsEnabled: boolean;
  smsTitle?: string;
  smsText?: string;
  target?: any;
  tour?: any;
  actionPathFinished?: boolean;
  ratingEnabled?: boolean;
  startingPoint?: any;
  rtl?: boolean;
  country: string;
  policy: string;
};

export const ShareContainer = (props: Props) => {
  const {
    popupTitle,
    qrEnabled,
    qrTitle,
    smsEnabled,
    smsTitle,
    smsText,
    target,
    tour,
    actionPathFinished,
    ratingEnabled,
    startingPoint,
    rtl,
    country,
    policy,
  } = props;
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState();
  const [isValid, setIsValid] = useState(false);
  const translation = useNavigationStore((state) => state.navigationTranslation);

  const mobileLink = useMemo(() => {
    const startingPointId =
      startingPoint?.tag?.defaultSid || startingPoint?.station?.id || startingPoint?.id;
    const targetId =
      target?.tag?.defaultSid || target?.tag?.sid || target?.station?.id || target?.id;
    return `${window.location.origin + window.location.pathname.replace('kiosk', 'tour')}?start=${startingPointId}&target=${targetId}`;
  }, [target, startingPoint]);

  const handleMobileNumberChange = (value, data) => {
    setPhoneNumber(value);
    setCountryCode(data.dialCode);
    const isValidNumber = value.length > 10 && value.length < 15;
    setIsValid(isValidNumber);
  };

  const handleSendSms = async () => {
    const smsResult = await AdminAPI.sendNavigationLinkSms({ mobileLink, phoneNumber, smsText });
    if (smsResult?.message === 'Success') {
      toast.success(translation?.navSmsSent || 'SMS sent successfully');
    } else {
      toast.error(translation?.navSmsError || 'Error sending message');
    }
    setPhoneNumber(countryCode);
    setIsValid(false);
  };

  return (
    <Stack
      sx={{
        background: '#FFF',
        alignItems: 'center',
        padding: '14px 10px',
        gap: '10px',
        textAlign: 'center',
        borderRadius: '10px',
        overflowX: 'hidden',
      }}
    >
      <Stack>
        <RatingTooltip
          tour={tour}
          target={target}
          actionPathFinished={!actionPathFinished}
          ratingEnabled={ratingEnabled}
          isShare
          rtl={rtl}
        >
          <Typography
            fontSize="16px"
            fontWeight={rtl ? 500 : 600}
            color="#1E1E1E"
            sx={{ direction: rtl ? 'rtl' : 'ltr' }}
          >
            {popupTitle}
          </Typography>
        </RatingTooltip>
        {qrEnabled && (
          <Typography
            fontSize="12px"
            fontWeight={rtl ? 400 : 500}
            color="#667085"
            sx={{ direction: rtl ? 'rtl' : 'ltr' }}
          >
            {qrTitle}
          </Typography>
        )}
      </Stack>
      {qrEnabled && (
        <Stack
          sx={{
            borderRadius: '10px',
            border: '1px solid var(--Gray-200, #EAECF0)',
            padding: '10px',
          }}
        >
          <img
            alt="qr"
            src={`https://api.qrserver.com/v1/create-qr-code/?size=166x166&data=${encodeURIComponent(mobileLink)}`}
            width="166px"
            height="166px"
          />
        </Stack>
      )}
      {smsEnabled && (
        <>
          {qrEnabled && smsEnabled && (
            <Stack sx={{ background: '#FFF', position: 'relative', width: '310px' }}>
              <Typography
                fontSize="12px"
                fontWeight={rtl ? 400 : 500}
                mx="auto"
                color="#667085"
                sx={{
                  zIndex: 10,
                  padding: '0px 10px',
                  width: 'max-content',
                  backgroundColor: '#fff',
                  direction: rtl ? 'rtl' : 'ltr',
                }}
              >
                {smsTitle}
              </Typography>
              <Divider style={{ margin: 0, width: '310px', position: 'absolute', top: '50%' }} />
            </Stack>
          )}
          <Stack sx={{ maxWidth: '372px', width: '100%', position: 'relative' }} direction="row">
            <StyledPhoneInput
              country={country?.toLowerCase() || 'il'}
              specialLabel=""
              placeholder=""
              value={phoneNumber}
              onChange={handleMobileNumberChange}
              enableSearch
              searchStyle={{ margin: '0px', padding: '0px' }}
              inputStyle={{
                height: '40px',
                borderRadius: '8px',
                width: '274px',
                marginRight: 'auto',
                marginLeft: 'auto',
                fontWeight: '500',
                fontSize: '14px',
              }}
              buttonStyle={{ paddingLeft: '14px' }}
              sx={{ fontSize: '14px', fontWeight: '500' }}
              countryCodeEditable={false}
            />
            <IconButton
              edge="end"
              style={{
                width: '36px',
                height: '40px',
                position: 'absolute',
                right: '20px',
                borderRadius: '8px',
                borderLeft: '1px solid var(--Gray-100, #F2F4F7)',
                borderTopLeftRadius: '1px',
                borderBottomLeftRadius: '1px',
              }}
              onClick={handleSendSms}
              disabled={!isValid}
            >
              <SendIcon
                style={{
                  width: '20px',
                  height: '20px',
                  fill: isValid ? '#2055FF' : '#667085',
                }}
              />
            </IconButton>
          </Stack>
          <Stack>
            <Typography
              fontSize="12px"
              fontWeight={rtl ? 400 : 500}
              color="#667085"
              sx={{ direction: rtl ? 'rtl' : 'ltr' }}
            >
              {policy}
            </Typography>
          </Stack>
        </>
      )}
    </Stack>
  );
};
